import React, { useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ChatMessage from './ChatMessage';
import { RootState, AppDispatch } from '../../store/store';
import { importMessagesAsync } from '../../store/projectSlice';
import { ApiErrorData } from '../../services/api';
import { AVAILABLE_MODELS, AvailableModel, BEDROCK_MODELS, BedrockModel } from '../../constants/defaults';
import styles from './ImportModal.module.css';

interface CSVRecord {
  sender: string;
  text: string;
  timestamp?: string;
  createdAt?: string;
  [key: string]: string | undefined; // For any additional columns
}

interface ImportModalProps {
  open: boolean;
  onClose: (success?: boolean) => void;
}

interface ParsedMessage {
  content: string;
  isBot: boolean;
  timestamp: string;
  comment?: string;
  rating?: string;
}

const ImportModal: React.FC<ImportModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector((state: RootState) => state.projects.selectedProject);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedMessages, setParsedMessages] = useState<ParsedMessage[]>([]);
  const [viewMode, setViewMode] = useState<'raw' | 'parsed'>('parsed');
  const [selectedModel, setSelectedModel] = useState<AvailableModel | BedrockModel>(AVAILABLE_MODELS[0].value);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine if this is a RAG project
  const isRagProject = project?.chat_type === 'rag' || Boolean(project?.rag_vector_db);
  
  // Get appropriate model list based on project type
  const availableModels = isRagProject ? BEDROCK_MODELS : AVAILABLE_MODELS;
  const defaultModel = isRagProject ? BEDROCK_MODELS[0].value : AVAILABLE_MODELS[0].value;

  // Initialize selectedModel with correct default when project changes
  React.useEffect(() => {
    setSelectedModel(defaultModel);
  }, [defaultModel]);

  const parseTxtMessages = (content: string): ParsedMessage[] => {
    const messages: ParsedMessage[] = [];
    const blocks = content.split('~~~~~~~~~~~~~~~~~~~~~~').filter(block => block.trim());
    
    blocks.forEach(block => {
      const trimmedBlock = block.trim();
      if (trimmedBlock.startsWith('You said:')) {
        messages.push({
          content: trimmedBlock.replace('You said:', '').trim(),
          isBot: false,
          timestamp: new Date().toISOString()
        });
      } else {
        // Any non-user message is treated as an assistant message
        const content = trimmedBlock.startsWith('ChatGPT said:') 
          ? trimmedBlock.replace('ChatGPT said:', '').trim()
          : trimmedBlock;
        
        messages.push({
          content,
          isBot: true,
          timestamp: new Date().toISOString()
        });
      }
    });
    
    return messages;
  };

  const parseCSVMessages = (content: string): ParsedMessage[] => {
    try {
      // Process CSV as a whole string to handle multi-line fields properly
      const { headers, rows } = parseCSV(content);
      
      // Validate required columns
      const senderIndex = headers.findIndex(col => col.toLowerCase() === 'sender');
      const textIndex = headers.findIndex(col => col.toLowerCase() === 'text');
      const timestampIndex = headers.findIndex(col => col.toLowerCase() === 'timestamp');
      const createdAtIndex = headers.findIndex(col => col.toLowerCase() === 'createdat');
      const commentIndex = headers.findIndex(col => col.toLowerCase() === 'comment');
      const ratingIndex = headers.findIndex(col => col.toLowerCase() === 'rating');
      
      if (senderIndex === -1 || textIndex === -1) {
        throw new Error('CSV must contain "sender" and "text" columns');
      }
      
      if (rows.length === 0) {
        throw new Error('CSV must have at least one data row');
      }
      
      // Process data rows
      const records: CSVRecord[] = [];
      
      for (const row of rows) {
        if (row.length >= Math.max(senderIndex, textIndex) + 1) {
          records.push({
            sender: row[senderIndex],
            text: row[textIndex],
            timestamp: timestampIndex !== -1 && timestampIndex < row.length ? row[timestampIndex] : undefined,
            createdAt: createdAtIndex !== -1 && createdAtIndex < row.length ? row[createdAtIndex] : undefined,
            comment: commentIndex !== -1 && commentIndex < row.length ? row[commentIndex] : undefined,
            rating: ratingIndex !== -1 && ratingIndex < row.length ? row[ratingIndex] : undefined
          });
        }
      }

      if (records.length === 0) {
        throw new Error('No valid data rows found in CSV');
      }

      return records.map(record => {
        // Choose provided timestamp or fallback to createdAt
        let rawTs = (record.timestamp || record.createdAt || '').trim();
        // Normalize common 'YYYY-MM-DD HH:mm:ss' format to ISO when timestamp is missing
        if (!record.timestamp && record.createdAt) {
          const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})\s+([0-9]{2}):([0-9]{2}):([0-9]{2})$/.exec(record.createdAt.trim());
          if (m) {
            const [, y, mo, d, h, mi, s] = m;
            const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
            rawTs = dt.toISOString();
          }
        }
        const timestamp = rawTs || new Date().toISOString();

        return {
          content: record.text,
          isBot: record.sender.toLowerCase() !== 'you',
          timestamp,
          // Only include comment if it exists and is not empty
          comment: record.comment && record.comment.trim() !== '' ? record.comment : undefined,
          // Only include rating if it's a valid value
          rating: ['none', 'thumbs_up', 'thumbs_down'].includes(record.rating || '') ? record.rating : undefined
        };
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      throw new Error('Invalid CSV format. Please check the file and try again.');
    }
  };

  // Helper function to parse CSV properly (handles quoted fields with commas and newlines)
  const parseCSV = (csvContent: string): { headers: string[], rows: string[][] } => {
    // Normalize line endings
    const content = csvContent.replace(/\r\n?/g, '\n');
    
    const result: string[][] = [];
    let row: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = i < content.length - 1 ? content[i + 1] : '';
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote inside quotes
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        row.push(currentField.trim());
        currentField = '';
      } else if (char === '\n' && !inQuotes) {
        // End of row
        row.push(currentField.trim());
        if (row.some(field => field !== '')) { // Skip empty rows
          result.push(row);
        }
        row = [];
        currentField = '';
      } else {
        // Regular character
        currentField += char;
      }
    }
    
    // Add the last field and row if there's any content
    if (currentField || row.length > 0) {
      row.push(currentField.trim());
      if (row.some(field => field !== '')) {
        result.push(row);
      }
    }
    
    // Extract headers and rows
    if (result.length === 0) {
      return { headers: [], rows: [] };
    }
    
    return {
      headers: result[0],
      rows: result.slice(1)
    };
  };
  
  const parseMessages = useCallback((content: string, fileName: string): ParsedMessage[] => {
    const isCSV = fileName.toLowerCase().endsWith('.csv');
    const parsed = isCSV ? parseCSVMessages(content) : parseTxtMessages(content);
    // Ensure chronological order for preview and subsequent import.
    // Use a stable sort with original index as tie-breaker to preserve order when timestamps are equal (common in TXT imports).
    const withIndex = parsed.map((m, i) => ({ m, i }));
    withIndex.sort((a, b) => {
      const ta = new Date(a.m.timestamp).getTime();
      const tb = new Date(b.m.timestamp).getTime();
      const da = Number.isFinite(ta) ? ta : Number.POSITIVE_INFINITY;
      const db = Number.isFinite(tb) ? tb : Number.POSITIVE_INFINITY;
      if (da === db) return a.i - b.i;
      return da - db;
    });
    return withIndex.map(x => x.m);
  }, [parseCSVMessages]);

  const handleFileRead = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setFileContent(content);
        setParsedMessages(parseMessages(content, file.name));
        setFileName(file.name);
        setError(null);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to parse file');
        setParsedMessages([]);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setParsedMessages([]);
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileRead(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileRead(e.target.files[0]);
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = async () => {
    if (!project?.id || !parsedMessages.length) return;

    try {
      const messagesToImport = parsedMessages.map(msg => ({
        text: msg.content,
        sender: msg.isBot ? selectedModel : 'You',
        timestamp: msg.timestamp,
        isBot: msg.isBot,
        projectId: project.id,
        comment: msg.comment,
        rating: msg.rating as 'none' | 'thumbs_up' | 'thumbs_down' | undefined
      }));

      await dispatch(importMessagesAsync({
        projectId: project.id,
        messages: messagesToImport
      })).unwrap();
      
      onClose(true); // Success - close modal
    } catch (error: unknown) {
      if (isApiError(error)) {
        console.error('ApiError details:', {
          message: error.message,
          status: error.status,
          code: error.code,
          data: error.data
        });
        
        switch (error.status) {
          case 413:
            setError('The import file is too large. Please try splitting it into smaller chunks.');
            break;
          case 401:
            setError('Your session has expired. Please refresh the page.');
            break;
          case 503:
          case 504:
            setError('Unable to connect to server. Please check your connection and try again.');
            break;
          default:
            setError(error.message || 'Failed to import messages. Please try again.');
        }
      } else {
        console.error('Error did not match ApiErrorData interface');
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleClose = () => {
    // Clear state
    setFileContent(null);
    setFileName(null);
    setParsedMessages([]);
    setViewMode('parsed');
    setSelectedModel(AVAILABLE_MODELS[0].value);
    setError(null);
    onClose(false);
  };

  const isApiError = (err: unknown): err is ApiErrorData => {
    const isValid = typeof err === 'object' && err !== null && 
      'message' in err && 'status' in err;
    console.error('Type guard check:', { isValid, err });
    return isValid;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      classes={{ paper: styles.dialogPaper }}
    >
      <DialogTitle>
        Import Chat History
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogTitle>
      <DialogContent>
        <Box className={styles.contentWrapper} sx={{ overflowY: 'auto', maxHeight: 'calc(80vh - 64px)' }}>
          <Box className={styles.importControls}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".txt,.csv,text/csv,text/plain"
              style={{ display: 'none' }}
            />
            
            <Paper
              className={styles.dropzone}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleDropzoneClick}
            >
              <CloudUploadIcon className={styles.uploadIcon} />
              <Typography variant="h6">
                {fileName || 'Drag and drop your chat export file here'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {fileName ? 'Drop another file to replace' : 'or click to select a file'}
              </Typography>
            </Paper>

            <TextField
              select
              fullWidth
              label={`Model for Assistant Messages${isRagProject ? ' (RAG)' : ''}`}
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value as AvailableModel | BedrockModel)}
              variant="outlined"
              size="small"
            >
              {availableModels.map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {fileContent && (
            <Box className={styles.tabsContainer}>
              <Tabs
                value={viewMode}
                onChange={(_, newValue) => setViewMode(newValue)}
                aria-label="preview mode"
              >
                <Tab label="Parsed Messages" value="parsed" />
                <Tab label="Raw Text" value="raw" />
              </Tabs>
            </Box>
          )}

          <Paper className={styles.preview}>
            <Box className={styles.previewContent}>
              {fileContent ? (
                viewMode === 'raw' ? (
                  <pre className={styles.preText}>
                    {fileContent}
                  </pre>
                ) : (
                  <Box className={styles.messages}>
                    {parsedMessages.map((msg, index) => (
                      <ChatMessage
                        key={index}
                        message={msg.content}
                        sender={msg.isBot ? selectedModel : 'You'}
                        timestamp={msg.timestamp}
                        isBot={msg.isBot}
                      />
                    ))}
                  </Box>
                )
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Supported formats:
                  <ul style={{ margin: '4px 0 0 20px' }}>
                    <li>.txt files from ChatGPT Chat Save plugin</li>
                    <li>.csv files with 'sender' and 'text' columns (and optional 'created_at')</li>
                  </ul>
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!parsedMessages.length}
          onClick={handleImport}
        >
          Import {parsedMessages.length} Messages
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportModal;
